import React, { useState, useEffect } from "react";

const Portfolio = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Unbounded:wght@300;400;600;700;900&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&family=DM+Serif+Display:ital@0;1&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --black:   #0A0A0A;
      --dark:    #111111;
      --card:    #161616;
      --border:  #222222;
      --border2: #2A2A2A;
      --lime:    #C8FF00;
      --lime2:   #A8E600;
      --lime-bg: rgba(200,255,0,0.06);
      --lime-bg2:rgba(200,255,0,0.12);
      --white:   #F5F5F0;
      --gray:    #888888;
      --gray2:   #555555;
    }

    html { scroll-behavior: smooth; }

    .root {
      font-family: 'DM Sans', sans-serif;
      background: var(--black);
      color: var(--white);
      min-height: 100vh;
      overflow-x: hidden;
    }

    .noise {
      position: fixed; inset: 0; z-index: 0; pointer-events: none; opacity: 0.035;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
      background-size: 200px 200px;
    }

    /* ── NAV — no name, just logo mark + links ── */
    .nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      padding: 0 48px; height: 72px;
      display: flex; align-items: center; justify-content: space-between;
      background: rgba(10,10,10,0.85);
      backdrop-filter: blur(20px);
      border-bottom: 1px solid var(--border);
    }
    .nav-mark {
      width: 36px; height: 36px; border-radius: 4px;
      background: var(--lime);
      display: flex; align-items: center; justify-content: center;
      font-family: 'Unbounded', sans-serif;
      font-size: 14px; font-weight: 900; color: var(--black);
      letter-spacing: -1px; flex-shrink: 0;
    }
    .nav-links { display: flex; gap: 36px; }
    .nav-link {
      font-size: 12px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase;
      color: var(--gray); cursor: pointer; transition: color 0.2s; text-decoration: none;
    }
    .nav-link:hover { color: var(--white); }
    @media(max-width:768px){ .nav { padding: 0 24px; } .nav-links { display: none; } }

    /* ── HERO ── */
    .hero {
      position: relative; z-index: 1;
      min-height: 100vh;
      display: grid; grid-template-columns: 1fr 1fr;
      padding: 72px 0 0; overflow: hidden;
    }
    @media(max-width:900px){ .hero { grid-template-columns: 1fr; } .hero-img-col { display: none; } }

    .hero-left {
      display: flex; flex-direction: column; justify-content: center;
      padding: 80px 64px 80px 7vw; position: relative; z-index: 2;
    }
    .hero-tag {
      display: inline-flex; align-items: center; gap: 8px;
      font-family: 'Unbounded', sans-serif;
      font-size: 9px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase;
      color: var(--lime); background: var(--lime-bg);
      border: 1px solid rgba(200,255,0,0.2); border-radius: 2px;
      padding: 8px 16px; margin-bottom: 40px; width: fit-content;
    }
    .tag-blink { width: 6px; height: 6px; border-radius: 50%; background: var(--lime); animation: blink 1.2s ease-in-out infinite; }
    @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }

    .hero-pre {
      font-family: 'DM Serif Display', serif; font-style: italic;
      font-size: clamp(16px, 2vw, 20px); color: var(--gray);
      margin-bottom: 8px; letter-spacing: 0.3px;
    }

    /* ── NAME — appears ONLY HERE, once ── */
    .hero-name {
      font-family: 'Unbounded', sans-serif;
      font-size: clamp(42px, 5.5vw, 78px); font-weight: 900;
      line-height: 0.95; letter-spacing: -2px;
      color: var(--white); margin-bottom: 24px;
    }
    .hero-name .hl { display: block; color: var(--lime); -webkit-text-stroke: 1px var(--lime); }

    .hero-role {
      font-size: clamp(14px, 1.5vw, 16px); font-weight: 300; line-height: 1.8;
      color: var(--gray); max-width: 420px; margin-bottom: 64px;
      border-left: 2px solid var(--lime); padding-left: 20px;
    }
    .hero-role strong { color: var(--white); font-weight: 500; }

    .hero-counters { display: flex; border: 1px solid var(--border); border-radius: 4px; overflow: hidden; width: fit-content; }
    .counter { padding: 20px 32px; text-align: center; border-right: 1px solid var(--border); position: relative; }
    .counter:last-child { border-right: none; }
    .counter::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: var(--lime); }
    .counter-num { font-family: 'Unbounded', sans-serif; font-size: 30px; font-weight: 900; color: var(--white); line-height: 1; }
    .counter-label { font-size: 10px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: var(--gray); margin-top: 6px; }

    /* ── HERO IMAGE COL — no name label ── */
    .hero-img-col { position: relative; overflow: hidden; background: var(--dark); }
    .hero-img-col::before {
      content: ''; position: absolute; inset: 0; z-index: 1;
      background: linear-gradient(90deg, var(--black) 0%, transparent 30%);
    }
    .hero-img-inner { position: absolute; inset: 0; z-index: 0; display: flex; align-items: center; justify-content: center; }
    .profile-photo { width: 60%; height: 100%; object-fit: cover; object-position: center top; filter: grayscale(30%) contrast(1.05); }
    .photo-placeholder {
      width: 100%; height: 100%;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      background: linear-gradient(160deg, #161616 0%, #0f1a0f 100%);
      position: relative; overflow: hidden;
    }
    .photo-placeholder::before {
      content: ''; position: absolute; inset: 0;
      background: repeating-linear-gradient(0deg, transparent, transparent 60px, rgba(200,255,0,0.03) 60px, rgba(200,255,0,0.03) 61px);
    }
    .avatar-ring {
      width: 220px; height: 220px; border-radius: 50%;
      border: 2px solid rgba(200,255,0,0.3);
      display: flex; align-items: center; justify-content: center;
      position: relative; z-index: 1;
      animation: spin-ring 12s linear infinite;
    }
    @keyframes spin-ring {
      from { box-shadow: 0 0 0 0 rgba(200,255,0,0.15); }
      50%  { box-shadow: 0 0 40px 8px rgba(200,255,0,0.15); }
      to   { box-shadow: 0 0 0 0 rgba(200,255,0,0.15); }
    }
    .avatar-inner {
      width: 190px; height: 190px; border-radius: 50%;
      background: linear-gradient(145deg, #1e2e1e, #0e1a0e);
      border: 1px solid rgba(200,255,0,0.15);
      display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden;
    }
    /* Flutter icon instead of initials */
    .avatar-flutter {
      font-size: 72px; position: relative; z-index: 1;
      filter: drop-shadow(0 0 24px rgba(200,255,0,0.5));
    }
    .avatar-glow {
      position: absolute; inset: 0; border-radius: 50%;
      background: radial-gradient(circle at 50% 60%, rgba(200,255,0,0.15), transparent 70%);
    }
    /* Role strip — no name, just role */
    .avatar-role-strip {
      position: absolute; bottom: 60px; left: 0; right: 0; z-index: 2;
      background: rgba(10,10,10,0.8); backdrop-filter: blur(10px);
      border-top: 1px solid rgba(200,255,0,0.2); border-bottom: 1px solid rgba(200,255,0,0.2);
      padding: 12px 32px; text-align: center;
    }
    .avatar-role-text { font-size: 11px; color: var(--lime); letter-spacing: 3px; text-transform: uppercase; }

    .hero-decoration {
      position: absolute; top: 100px; right: 40px; z-index: 3;
      display: flex; flex-direction: column; gap: 6px;
    }
    .deco-chip {
      background: rgba(10,10,10,0.9); backdrop-filter: blur(10px);
      border: 1px solid var(--border2); border-radius: 4px;
      padding: 10px 16px; display: flex; align-items: center; gap: 10px;
      font-size: 12px; color: var(--white); font-weight: 500;
      animation: float 3s ease-in-out infinite;
    }
    .deco-chip:nth-child(2) { animation-delay: 0.5s; }
    .deco-chip:nth-child(3) { animation-delay: 1s; }
    @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
    .deco-chip-icon { font-size: 16px; }
    .deco-chip-lime { color: var(--lime); font-weight: 700; font-size: 11px; }

    /* ── MARQUEE ── */
    .marquee-wrap {
      position: relative; z-index: 1;
      border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
      background: var(--dark); padding: 18px 0; overflow: hidden;
    }
    .marquee-track { display: flex; width: max-content; animation: marquee 20s linear infinite; }
    @keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
    .marquee-item {
      display: flex; align-items: center; gap: 20px; padding: 0 32px;
      font-family: 'Unbounded', sans-serif; font-size: 10px; font-weight: 600;
      letter-spacing: 2px; text-transform: uppercase; color: var(--gray2); white-space: nowrap;
    }
    .marquee-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--lime); flex-shrink: 0; }

    /* ── SECTIONS ── */
    .section { position: relative; z-index: 1; padding: 120px 7vw; }
    .section + .section { border-top: 1px solid var(--border); }
    .eyebrow {
      font-family: 'Unbounded', sans-serif;
      font-size: 9px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase;
      color: var(--lime); margin-bottom: 16px;
      display: flex; align-items: center; gap: 12px;
    }
    .eyebrow::before { content: ''; display: block; width: 24px; height: 1px; background: var(--lime); }
    .sec-title {
      font-family: 'Unbounded', sans-serif;
      font-size: clamp(32px, 4.5vw, 58px); font-weight: 900;
      color: var(--white); line-height: 1.0; letter-spacing: -2px; margin-bottom: 64px;
    }
    .sec-title em {
      font-style: italic; font-family: 'DM Serif Display', serif;
      color: var(--lime); font-weight: 400; letter-spacing: -1px;
    }

    /* ABOUT */
    .about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start; }
    @media(max-width:768px){ .about-grid { grid-template-columns: 1fr; gap: 48px; } }
    .about-text { font-size: 17px; line-height: 2.0; color: var(--gray); font-weight: 300; }
    .about-text strong { color: var(--white); font-weight: 500; }
    .about-text p + p { margin-top: 24px; }
    .about-facts { display: flex; flex-direction: column; gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: 4px; overflow: hidden; }
    .fact-row { display: flex; padding: 20px 24px; background: var(--card); justify-content: space-between; align-items: center; transition: background 0.2s; }
    .fact-row:hover { background: #1c1c1c; }
    .fact-key { font-size: 11px; letter-spacing: 2px; text-transform: uppercase; color: var(--gray); font-weight: 500; }
    .fact-val { font-size: 13px; font-weight: 600; color: var(--white); }
    .fact-val.lime { color: var(--lime); }

    /* COMPETENCIES */
    .comp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 2px; background: var(--border); border: 1px solid var(--border); border-radius: 4px; overflow: hidden; }
    .comp-card { padding: 40px 36px; background: var(--card); position: relative; transition: background 0.25s; cursor: default; }
    .comp-card:hover { background: #1a1a1a; }
    .comp-card:hover .comp-accent { opacity: 1; }
    .comp-accent { position: absolute; top: 0; left: 0; right: 0; height: 2px; background: var(--lime); opacity: 0; transition: opacity 0.3s; }
    .comp-icon-wrap { width: 52px; height: 52px; border-radius: 4px; background: var(--lime-bg); border: 1px solid rgba(200,255,0,0.15); display: flex; align-items: center; justify-content: center; font-size: 24px; margin-bottom: 24px; }
    .comp-num { position: absolute; top: 28px; right: 28px; font-family: 'Unbounded', sans-serif; font-size: 11px; font-weight: 700; color: var(--border2); letter-spacing: 1px; }
    .comp-title { font-family: 'Unbounded', sans-serif; font-size: 14px; font-weight: 700; color: var(--white); margin-bottom: 12px; letter-spacing: -0.3px; }
    .comp-desc { font-size: 13px; color: var(--gray); line-height: 1.8; }

    /* EXPERIENCE */
    .exp-wrap { display: grid; grid-template-columns: 280px 1fr; gap: 80px; }
    @media(max-width:768px){ .exp-wrap { grid-template-columns: 1fr; gap: 48px; } }
    .exp-org-badge { display: inline-flex; align-items: center; gap: 10px; padding: 10px 16px; border: 1px solid var(--border); border-radius: 4px; background: var(--card); margin-bottom: 28px; font-size: 12px; font-weight: 600; color: var(--white); letter-spacing: 0.5px; }
    .exp-org-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--lime); }
    .exp-meta { display: flex; flex-direction: column; gap: 14px; }
    .exp-meta-row { display: flex; flex-direction: column; gap: 4px; }
    .exp-meta-label { font-size: 9px; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gray2); font-weight: 600; }
    .exp-meta-val { font-size: 14px; font-weight: 500; color: var(--white); }
    .exp-meta-val.lime { color: var(--lime); }
    .exp-divider { width: 100%; height: 1px; background: var(--border); margin: 20px 0; }
    .exp-title { font-family: 'DM Serif Display', serif; font-style: italic; font-size: clamp(28px, 3.5vw, 42px); color: var(--white); margin-bottom: 8px; line-height: 1.1; }
    .exp-subtitle { font-size: 13px; color: var(--gray); letter-spacing: 1px; text-transform: uppercase; margin-bottom: 40px; }
    .exp-items { display: flex; flex-direction: column; gap: 2px; background: var(--border); border: 1px solid var(--border); border-radius: 4px; overflow: hidden; }
    .exp-item { display: flex; gap: 20px; align-items: flex-start; padding: 20px 24px; background: var(--card); transition: background 0.2s; }
    .exp-item:hover { background: #1a1a1a; }
    .exp-arrow { width: 20px; height: 20px; border-radius: 2px; background: var(--lime-bg); border: 1px solid rgba(200,255,0,0.2); display: flex; align-items: center; justify-content: center; color: var(--lime); font-size: 10px; font-weight: 900; flex-shrink: 0; margin-top: 1px; }
    .exp-item p { font-size: 14px; color: var(--gray); line-height: 1.7; }

    /* ACHIEVEMENTS */
    .ach-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 2px; background: var(--border); border: 1px solid var(--border); border-radius: 4px; overflow: hidden; }
    @media(max-width:900px){ .ach-grid { grid-template-columns: repeat(2,1fr); } }
    @media(max-width:480px){ .ach-grid { grid-template-columns: 1fr; } }
    .ach-card { padding: 52px 36px; background: var(--card); text-align: center; transition: background 0.2s; }
    .ach-card:hover { background: #1c1c1c; }
    .ach-num { font-family: 'Unbounded', sans-serif; font-size: 56px; font-weight: 900; color: var(--lime); line-height: 1; margin-bottom: 12px; text-shadow: 0 0 40px rgba(200,255,0,0.25); }
    .ach-title { font-size: 13px; font-weight: 600; color: var(--white); margin-bottom: 10px; }
    .ach-desc { font-size: 12px; color: var(--gray); line-height: 1.7; }

    /* CERTIFICATIONS */
    .cert-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    @media(max-width:768px){ .cert-grid { grid-template-columns: 1fr; } }
    .cert-card { padding: 36px 28px; background: var(--card); border: 1px solid var(--border); border-radius: 4px; position: relative; overflow: hidden; cursor: default; transition: border-color 0.3s, transform 0.3s; }
    .cert-card:hover { border-color: var(--lime); transform: translateY(-4px); }
    .cert-card::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px; background: var(--lime); transform: scaleX(0); transform-origin: left; transition: transform 0.4s; }
    .cert-card:hover::after { transform: scaleX(1); }
    .cert-icon-box { width: 56px; height: 56px; border-radius: 4px; margin-bottom: 24px; background: var(--lime-bg); border: 1px solid rgba(200,255,0,0.2); display: flex; align-items: center; justify-content: center; font-size: 26px; }
    .cert-name { font-family: 'Unbounded', sans-serif; font-size: 13px; font-weight: 700; color: var(--white); margin-bottom: 8px; line-height: 1.3; letter-spacing: -0.3px; }
    .cert-org { font-size: 11px; color: var(--gray); letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 20px; }
    .cert-badge { display: inline-block; padding: 4px 12px; border: 1px solid rgba(200,255,0,0.3); border-radius: 2px; font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--lime); background: var(--lime-bg); }

    /* SKILLS */
    .skills-layout { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px; background: var(--border); border: 1px solid var(--border); border-radius: 4px; overflow: hidden; }
    @media(max-width:700px){ .skills-layout { grid-template-columns: 1fr; } }
    .skill-group { padding: 40px 36px; background: var(--card); }
    .sg-title { font-family: 'Unbounded', sans-serif; font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--lime); margin-bottom: 24px; display: flex; align-items: center; gap: 10px; }
    .sg-title::after { content: ''; flex: 1; height: 1px; background: var(--border); }
    .sg-pills { display: flex; flex-wrap: wrap; gap: 8px; }
    .sg-pill { padding: 7px 14px; font-size: 12px; font-weight: 500; color: var(--gray); background: var(--black); border: 1px solid var(--border2); border-radius: 2px; transition: all 0.2s; cursor: default; }
    .sg-pill:hover { color: var(--white); border-color: var(--gray2); }
    .sg-pill.star { color: var(--lime); border-color: rgba(200,255,0,0.3); background: var(--lime-bg); }

    /* FOOTER — no name, no copyright with name */
    .footer { position: relative; z-index: 1; border-top: 1px solid var(--border); padding: 48px 7vw; }
    .footer-inner { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 24px; }
    .footer-role {
      font-family: 'Unbounded', sans-serif; font-size: 11px; font-weight: 700;
      letter-spacing: 3px; text-transform: uppercase; color: var(--lime);
    }
    .footer-tagline { font-size: 13px; color: var(--gray); margin-top: 6px; max-width: 340px; line-height: 1.6; }
    .footer-cert-row { display: flex; flex-direction: column; gap: 8px; }
    .footer-cert { display: flex; align-items: center; gap: 10px; padding: 10px 16px; background: var(--card); border: 1px solid var(--border); border-radius: 2px; font-size: 12px; color: var(--gray); font-weight: 500; }
    .footer-cert-icon { font-size: 16px; }
    .footer-bottom { display: flex; justify-content: space-between; align-items: center; padding-top: 24px; margin-top: 32px; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 12px; }
    .footer-copy { font-size: 12px; color: var(--gray2); }
    .footer-loc { font-size: 12px; color: var(--gray2); display: flex; align-items: center; gap: 6px; }
    .footer-loc::before { content: '📍'; font-size: 12px; }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--black); }
    ::-webkit-scrollbar-thumb { background: var(--lime); border-radius: 2px; }
  `;

  const marqueeItems = ["Flutter", "Dart", "Node.js", "Express.js", "MongoDB", "AWS", "REST APIs", "Cross-Platform", "Mobile Dev", "Technical Training", "CI/CD", "Firebase"];

  const competencies = [
    { icon: "📱", num: "01", title: "Flutter Development", desc: "Expert in pixel-perfect, high-performance cross-platform apps for Android & iOS. Custom widgets, complex animations, advanced UI/UX patterns." },
    { icon: "⚙️", num: "02", title: "Back-End Development", desc: "Node.js & Express.js for scalable REST APIs, secure JWT auth flows, and robust server-side systems for mobile frontends." },
    { icon: "🎓", num: "03", title: "Technical Training", desc: "Hands-on trainer for 300+ students. Architecture, state management, API integration, production deployment — comprehensive curriculum." },
    { icon: "☁️", num: "04", title: "Cloud & DevOps", desc: "AWS Cloud Practitioner certified. EC2, S3, CI/CD pipelines, Nginx, and scalable production environment management." },
    { icon: "🗄️", num: "05", title: "Database Engineering", desc: "MongoDB Associate Developer certified. Schema design, aggregation pipelines, indexing, and full mobile/server integration." },
  ];

  const experience = [
    "Teach Flutter & Dart to UG/PG students — widget architecture, state management, and advanced mobile UI design.",
    "Develop comprehensive curriculum covering Android & iOS deployment, animations, and performance optimization.",
    "Conduct intensive workshops on API integration, REST backends, MongoDB, and Firebase for mobile-first apps.",
    "Guide students through end-to-end projects from wireframe to Play Store and App Store deployment.",
    "Conduct code reviews focused on clean architecture, SOLID principles, and real-world debugging strategies.",
    "Mentor capstone projects integrating Flutter with Node.js backends, cloud services, and third-party APIs.",
  ];

  return (
    <>
      <style>{css}</style>
      <div className="root">
        <div className="noise" />

        {/* NAV — lime square mark only, no name */}
        <nav className="nav">
          <div className="nav-mark">F</div>
          <div className="nav-links">
            {["About", "Skills", "Experience", "Certifications"].map(l => (
              <a key={l} className="nav-link" href={`#${l.toLowerCase()}`}>{l}</a>
            ))}
          </div>
        </nav>

        {/* HERO — name appears HERE and ONLY HERE */}
        <section className="hero">
          <div className="hero-left">
            <div className="hero-tag">
              <div className="tag-blink" />
              Available for Projects
            </div>
            <div className="hero-pre">Hello, I'm</div>
            <h1 className="hero-name">
              Venkata<span className="hl">krishna</span>
            </h1>
            <p className="hero-role">
              <strong>Flutter Developer</strong> &amp; Technical Trainer building
              cross-platform excellence for <strong>3+ years</strong> — Android,
              iOS, Node.js backends, and cloud-ready deployments.
            </p>
            <div className="hero-counters">
              {[["3+","Yrs Exp"],["300+","Students"],["3","Certs"]].map(([n,l]) => (
                <div className="counter" key={l}>
                  <div className="counter-num">{n}</div>
                  <div className="counter-label">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right col — photo + role only, no name */}
          <div className="hero-img-col">
            <div className="hero-img-inner">
              <img className="profile-photo" src="https://adityauniversity.in/adtppu/SDG/5519.png" alt="profile" />
              <div className="photo-placeholder">
                <div className="avatar-ring">
                  <div className="avatar-inner">
                    <div className="avatar-glow" />
                    <div className="avatar-flutter">📱</div>
                  </div>
                </div>
                <div className="avatar-role-strip">
                  <div className="avatar-role-text">Flutter Developer</div>
                </div>
              </div>
            </div>
            <div className="hero-decoration">
              <div className="deco-chip"><span className="deco-chip-icon">📱</span><span>Flutter</span><span className="deco-chip-lime">Expert</span></div>
              <div className="deco-chip"><span className="deco-chip-icon">☁️</span><span>AWS</span><span className="deco-chip-lime">Certified</span></div>
              <div className="deco-chip"><span className="deco-chip-icon">🍃</span><span>MongoDB</span><span className="deco-chip-lime">Associate</span></div>
            </div>
          </div>
        </section>

        {/* MARQUEE */}
        <div className="marquee-wrap">
          <div className="marquee-track">
            {[...marqueeItems, ...marqueeItems].map((item, i) => (
              <div className="marquee-item" key={i}><div className="marquee-dot" />{item}</div>
            ))}
          </div>
        </div>

        {/* ABOUT */}
        <section className="section" id="about">
          <div className="eyebrow">01 — About Me</div>
          <h2 className="sec-title">Who I <em>Am</em></h2>
          <div className="about-grid">
            <div className="about-text">
              <p>A <strong>dedicated Flutter Developer</strong> with over 3 years of experience crafting beautiful, high-performance cross-platform mobile applications. Specializing in seamless experiences for both <strong>Android and iOS</strong> from a single codebase, paired with powerful <strong>Node.js and Express.js backends</strong> and cloud-ready deployments.</p>
              <p>Deeply invested in <strong>shaping the next generation</strong> of mobile engineers — having trained over <strong>300+ students</strong> at Aditya University. Certified by <strong>MongoDB, AWS, and Certiport</strong>.</p>
            </div>
            <div className="about-facts">
              {[
                ["Specialization", "Flutter & Dart", true],
                ["Backend", "Node.js · Express.js", false],
                ["Institution", "Aditya University", false],
                ["Location", "Kakinada, Andhra Pradesh", false],
                ["Experience", "3+ Years", true],
                ["Certifications", "3 Earned", true],
              ].map(([k, v, l]) => (
                <div className="fact-row" key={k}>
                  <span className="fact-key">{k}</span>
                  <span className={`fact-val${l ? " lime" : ""}`}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* COMPETENCIES */}
        <section className="section" id="skills">
          <div className="eyebrow">02 — Expertise</div>
          <h2 className="sec-title">Core <em>Competencies</em></h2>
          <div className="comp-grid">
            {competencies.map((c, i) => (
              <div className="comp-card" key={i}>
                <div className="comp-accent" />
                <div className="comp-num">{c.num}</div>
                <div className="comp-icon-wrap">{c.icon}</div>
                <div className="comp-title">{c.title}</div>
                <div className="comp-desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* EXPERIENCE */}
        <section className="section" id="experience">
          <div className="eyebrow">03 — Experience</div>
          <h2 className="sec-title">Professional <em>Journey</em></h2>
          <div className="exp-wrap">
            <div>
              <div className="exp-org-badge"><div className="exp-org-dot" />Aditya University</div>
              <div className="exp-meta">
                <div className="exp-meta-row"><span className="exp-meta-label">Location</span><span className="exp-meta-val">Kakinada, AP</span></div>
                <div className="exp-divider" />
                <div className="exp-meta-row"><span className="exp-meta-label">Period</span><span className="exp-meta-val">2022 — Present</span></div>
                <div className="exp-divider" />
                <div className="exp-meta-row"><span className="exp-meta-label">Duration</span><span className="exp-meta-val lime">3+ Years</span></div>
                <div className="exp-divider" />
                <div className="exp-meta-row"><span className="exp-meta-label">Status</span><span className="exp-meta-val lime">Active</span></div>
              </div>
            </div>
            <div>
              <div className="exp-title">Lecturer &amp; Flutter Developer</div>
              <div className="exp-subtitle">Full Stack Mobile Development · Flutter, Node.js &amp; Beyond</div>
              <div className="exp-items">
                {experience.map((item, i) => (
                  <div className="exp-item" key={i}><div className="exp-arrow">→</div><p>{item}</p></div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ACHIEVEMENTS */}
        <section className="section" id="achievements">
          <div className="eyebrow">04 — Impact</div>
          <h2 className="sec-title">Key <em>Numbers</em></h2>
          <div className="ach-grid">
            {[
              ["300+", "Students Trained", "Mentored over 300 students — many have shipped apps to Play Store and App Store."],
              ["3+",   "Years of Excellence", "Consistently delivering high-quality instruction with top student feedback."],
              ["3",    "Certifications", "MongoDB, AWS, and Certiport certified across the full stack."],
              ["2",    "Platforms Shipped", "Every app ships natively to both Android and iOS from one clean codebase."],
            ].map(([n, t, d], i) => (
              <div className="ach-card" key={i}>
                <div className="ach-num">{n}</div>
                <div className="ach-title">{t}</div>
                <div className="ach-desc">{d}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CERTIFICATIONS */}
        <section className="section" id="certifications">
          <div className="eyebrow">05 — Credentials</div>
          <h2 className="sec-title"><em>Certifi</em>cations</h2>
          <div className="cert-grid">
            {[
              { icon: "🍃", name: "Associate Developer — MongoDB", org: "MongoDB Inc." },
              { icon: "☁️", name: "Cloud Practitioner — AWS", org: "Amazon Web Services" },
              { icon: "⚡", name: "IT Specialist — JavaScript", org: "Certiport" },
            ].map((c, i) => (
              <div className="cert-card" key={i}>
                <div className="cert-icon-box">{c.icon}</div>
                <div className="cert-name">{c.name}</div>
                <div className="cert-org">{c.org}</div>
                <div className="cert-badge">Verified</div>
              </div>
            ))}
          </div>
        </section>

        {/* SKILLS */}
        <section className="section">
          <div className="eyebrow">06 — Skills</div>
          <h2 className="sec-title">Technical <em>Stack</em></h2>
          <div className="skills-layout">
            {[
              { label: "Mobile · Flutter", pills: [["Flutter","star"],["Dart","star"],["BLoC","star"],["Provider",""],["GetX",""],["Animations",""],["Android",""],["iOS",""]] },
              { label: "Back-End", pills: [["Node.js","star"],["Express.js","star"],["REST APIs","star"],["JWT Auth",""],["Middleware",""],["Socket.io",""]] },
              { label: "Database", pills: [["MongoDB","star"],["Mongoose","star"],["Aggregation",""],["Indexing",""],["Schema Design",""],["Firebase",""]] },
              { label: "Cloud & DevOps", pills: [["AWS EC2",""],["AWS S3",""],["Docker",""],["GitHub Actions",""],["CI/CD",""],["Nginx",""]] },
            ].map((g, i) => (
              <div className="skill-group" key={i}>
                <div className="sg-title">{g.label}</div>
                <div className="sg-pills">
                  {g.pills.map(([p, cls]) => (
                    <span key={p} className={`sg-pill${cls ? " " + cls : ""}`}>{p}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FOOTER — role + certs only, no name */}
        <footer className="footer">
          <div className="footer-inner">
            <div>
              <div className="footer-role">Flutter Developer · Technical Trainer</div>
              <div className="footer-tagline">Building cross-platform excellence at Aditya University, Kakinada.</div>
            </div>
            <div className="footer-cert-row">
              {[["🍃","MongoDB Associate"],["☁️","AWS Practitioner"],["⚡","Certiport JS"]].map(([icon,label]) => (
                <div className="footer-cert" key={label}><span className="footer-cert-icon">{icon}</span>{label}</div>
              ))}
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-copy">© 2024 · All rights reserved</div>
            <div className="footer-loc">Kakinada, Andhra Pradesh, India</div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Portfolio;
